import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import webSocketService from '@/services/websocket.service';
import { wasSelfMutationRecent, wasForeignChangeRecent, markForeignChange } from '@/services/selfMutationSignal';

// Live updates for an OPEN record surface. Subscribes to `record_change` WS
// events and invokes the callback when the event concerns what the surface
// displays. Matching criteria (any combination; all optional):
//   entityType + recordId      — one record (detail pages)
//   records: [{entityType, recordId}] or [{entityType, recordIds: []}]
//                              — several records at once (contact/company
//                                tabs, picking rows spanning many sales)
//   lineItemIds: []            — shared line items the surface renders
//                                (line items sit on several records at once,
//                                and the backend can't know who displays
//                                them — the client matches by id instead)
//
// The callback (events, isStale) should silently refetch and diff against the
// previous state (see diffRecordForHighlights) — the event is only a
// "something you display changed" signal, never the source of truth. Events
// are debounced so a composite update that logs many activity rows triggers
// ONE refetch. `isStale()` turns true once a NEWER flush has fired: check it
// after awaiting the refetch and skip applying, so a slow older response can
// never overwrite a fresher one.
//
// Own-actor events are NOT skipped on purpose: the same user may have the
// record open in another tab, and for the originating tab the refetch is a
// cheap no-op diff (optimistic updates already applied the change locally).
//
// Also fires on WS reconnect (with no events), because changes made while
// offline were missed and the surface must resync.
export const useRecordChanges = ({ entityType, recordId, lineItemIds, records, onRelevantChange }) => {
  const configRef = useRef({});
  configRef.current = { entityType, recordId, lineItemIds, records, onRelevantChange };

  useEffect(() => {
    let pendingEvents = [];
    let debounceTimer = null;
    let generation = 0;

    const invoke = (events) => {
      const gen = ++generation;
      const isStale = () => gen !== generation;
      if (configRef.current.onRelevantChange) configRef.current.onRelevantChange(events, isStale);
    };

    const flush = () => {
      debounceTimer = null;
      const events = pendingEvents;
      pendingEvents = [];
      invoke(events);
    };

    // The event carries both the numeric id and the hash (Q-…/S-…/R-…);
    // detail pages hold the record under its hash, lists under numeric ids —
    // match either so the caller can pass whatever id it has.
    const matchesId = (event, id) => id != null && (
      String(event.entity_id) === String(id)
      || (event.entity_hash != null && String(event.entity_hash) === String(id))
    );

    const isRelevant = (event) => {
      const { entityType: type, recordId: id, lineItemIds: itemIds, records: recs } = configRef.current;
      if (type && event.entity_type === type && matchesId(event, id)) return true;
      if (Array.isArray(recs) && recs.some(r => r && event.entity_type === r.entityType && (
        Array.isArray(r.recordIds) ? r.recordIds.some(rid => matchesId(event, rid)) : matchesId(event, r.recordId)
      ))) return true;
      return Array.isArray(event.line_item_ids) && Array.isArray(itemIds)
        && event.line_item_ids.some(liId => itemIds.includes(liId));
    };

    const hasAnyCriteria = () => {
      const { recordId: id, lineItemIds: itemIds, records: recs } = configRef.current;
      return id != null || (recs && recs.length > 0) || (itemIds && itemIds.length > 0);
    };

    const unsubscribeChanges = webSocketService.addRecordChangeListener((event) => {
      if (!isRelevant(event)) return;
      pendingEvents.push(event);
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(flush, 400);
    });

    const unsubscribeReconnect = webSocketService.addReconnectListener(() => {
      if (!hasAnyCriteria()) return;
      pendingEvents = [];
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = null;
      invoke([]);
    });

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      unsubscribeChanges();
      unsubscribeReconnect();
    };
  }, []);
};

// Fields worth a per-field yellow highlight on a line item.
const LINE_ITEM_HIGHLIGHT_FIELDS = [
  'price', 'currency_id', 'available', 'quantity', 'sku', 'defects',
  'component_id', 'public_note', 'invoice_note', 'defect_note',
];

// Image counts per gallery type on a raw line item ('customer' = client
// uploads, 'our' = dezShop photos) — the thumbnails show these counts.
const imageCount = (item, imageType) => (Array.isArray(item?.images)
  ? item.images.filter(img => img?.image_type === imageType).length
  : 0);

const valuesDiffer = (a, b) => {
  if (a === b) return false;
  if (a == null && b == null) return false;
  if (a && b && typeof a === 'object' && typeof b === 'object') {
    try { return JSON.stringify(a) !== JSON.stringify(b); } catch { return true; }
  }
  return true;
};

// Identity of a comment inside a comments JSON array — created_at is an ISO
// string set server-side at append time (normalizers rename it to timestamp).
const commentStamp = (c) => c?.created_at ?? c?.timestamp
  ?? `${c?.created_by_username ?? c?.username ?? ''}|${c?.comment_text ?? c?.message ?? ''}`;

// Stamps of comments present in the fresh array but not in the old one.
const newCommentStamps = (oldComments, newComments) => {
  const seen = new Set((oldComments || []).map(commentStamp));
  return (newComments || []).map(commentStamp).filter(s => !seen.has(s));
};

// Compare the previously displayed record with the freshly refetched one and
// return the Set of highlight keys for everything another user changed:
//   'status'                          — record status
//   'comments' / 'comment:<stamp>'    — record-level: any new comment / that comment
//   'lineItem:<id>'                   — row-level (new line item, or any change on it)
//   'lineItem:<id>:<field>'           — specific field (price, available, …)
//   'lineItem:<id>:comments' / 'lineItem:<id>:comment:<stamp>'
// `getLineItems` adapts per record shape (ReturnDetail nests them under
// return_line_items[].line_item).
// `includeNew`: ring a line item that wasn't in the old set as brand-new.
// TRUE only makes sense when both sides are the SAME record (a detail page:
// an item appearing really was added to that quote/sale/return). On LIST-like
// surfaces the row set changes with search/pagination/filters, so an item
// "not seen before" is merely newly VISIBLE — ringing it would light up every
// row on a new search. Those callers pass includeNew: false.
export const diffRecordForHighlights = (oldRecord, newRecord, { getLineItems, includeNew = true } = {}) => {
  const keys = new Set();
  if (!oldRecord || !newRecord) return keys;

  if (valuesDiffer(oldRecord.status, newRecord.status)) keys.add('status');
  for (const stamp of newCommentStamps(oldRecord.comments, newRecord.comments)) {
    keys.add('comments');
    keys.add(`comment:${stamp}`);
  }

  const pick = getLineItems || ((record) => record?.line_items);
  const oldItems = new Map((pick(oldRecord) || []).filter(Boolean).map(li => [li.id, li]));
  for (const item of pick(newRecord) || []) {
    if (!item) continue;
    const previous = oldItems.get(item.id);
    if (!previous) {
      // Brand-new line item: no specific field to flash — ring the whole row.
      // Skipped on list-like surfaces, where "not in the old set" just means a
      // different search/page (see includeNew).
      if (includeNew) {
        keys.add(`lineItem:${item.id}:new`);
        keys.add(`lineItem:${item.id}`);
      }
      continue;
    }
    for (const field of LINE_ITEM_HIGHLIGHT_FIELDS) {
      if (valuesDiffer(previous[field], item[field])) {
        keys.add(`lineItem:${item.id}:${field}`);
        keys.add(`lineItem:${item.id}`);
      }
    }
    for (const stamp of newCommentStamps(previous.comments, item.comments)) {
      keys.add(`lineItem:${item.id}:comments`);
      keys.add(`lineItem:${item.id}:comment:${stamp}`);
      keys.add(`lineItem:${item.id}`);
    }
    if (imageCount(previous, 'customer') !== imageCount(item, 'customer')) {
      keys.add(`lineItem:${item.id}:customerImages`);
      keys.add(`lineItem:${item.id}`);
    }
    if (imageCount(previous, 'our') !== imageCount(item, 'our')) {
      keys.add(`lineItem:${item.id}:dezshopImages`);
      keys.add(`lineItem:${item.id}`);
    }
    // Vehicle reassignment: the id lives under customer_vehicle_id and/or the
    // nested vehicle object, depending on the record shape.
    if (valuesDiffer(previous.customer_vehicle_id, item.customer_vehicle_id)
      || valuesDiffer(previous.vehicle?.id, item.vehicle?.id)) {
      keys.add(`lineItem:${item.id}:vehicle`);
      keys.add(`lineItem:${item.id}`);
    }
  }
  return keys;
};

// Stamps of the record-level comments to flash, for `<Comments highlightStamps>`.
export const getRecordCommentStamps = (highlights) => {
  const stamps = new Set();
  if (highlights) {
    for (const key of highlights) {
      if (key.startsWith('comment:')) stamps.add(key.slice('comment:'.length));
    }
  }
  return stamps;
};

// Visual for a value changed live by another user: quick yellow flash
// (tailwind keyframe highlightFlash) + a ring that lingers until the TTL.
// rounded-full matches the pill shape of the status/value badges it wraps;
// inline-flex shrink-wraps the wrapper span to the badge height (a plain
// inline span is as tall as the line-height, so the ring floats above).
export const LIVE_HIGHLIGHT_CLASS = 'inline-flex animate-highlightFlash rounded-full ring-4 ring-yellow-400/80';

// Per-line-item live-change flags derived from the page-level highlight keys,
// consumed by LineItem/LineItemDesktop. Undefined when nothing changed on the
// item, so the common case costs nothing.
export const createLiveHighlights = (highlights, itemId) => {
  if (!highlights || highlights.size === 0) return undefined;
  const has = (suffix) => highlights.has(`lineItem:${itemId}${suffix}`);
  if (!has('')) return undefined;
  const commentPrefix = `lineItem:${itemId}:comment:`;
  const commentStamps = new Set();
  for (const key of highlights) {
    if (key.startsWith(commentPrefix)) commentStamps.add(key.slice(commentPrefix.length));
  }
  return {
    // Ring the whole row ONLY for a brand-new line item; for edits the
    // specific value (price/comment/…) carries its own highlight.
    row: has(':new'),
    price: has(':price') || has(':currency_id'),
    quantity: has(':quantity'),
    component: has(':component_id'),
    vehicle: has(':vehicle'),
    sku: has(':sku'),
    uzura: has(':defects'),
    available: has(':available'),
    comments: has(':comments'),
    customerImages: has(':customerImages'),
    dezshopImages: has(':dezshopImages'),
    commentStamps,
  };
};

// Holds the currently-highlighted keys. Highlights PERSIST until the user
// reloads the page — no auto-expiry: the yellow outline stays so a change is
// never missed just because the user glanced away or the fade timed out. The
// bg-flash animation still plays once (CSS keyframe); only the ring lingers. A
// page refresh clears everything.
export const useChangeHighlights = () => {
  const [highlights, setHighlights] = useState(() => new Set());

  const addHighlights = (keys) => {
    // Don't flash the user's OWN edits on the tab where they made them: a
    // self-originated CRM mutation from this tab, moments ago, whose echo
    // triggered this diff. A different tab (even same user) didn't mutate, so
    // it still flashes. BUT never let this swallow a COLLEAGUE's change that
    // arrived in the same window — suppress only when we self-mutated AND no
    // foreign change is in flight. See selfMutationSignal.js.
    if (wasSelfMutationRecent() && !wasForeignChangeRecent()) return;
    const added = [...keys];
    if (!added.length) return;
    setHighlights(prev => {
      const next = new Set(prev);
      for (const key of added) next.add(key);
      return next;
    });
  };

  return { highlights, addHighlights };
};

// For LIST pages, which diff their query-cache data on every change: returns
// a predicate telling whether a record_change WS event arrived recently. Own
// optimistic cache updates happen with no preceding event, so gating the diff
// on this keeps your own click from flashing yellow — while remote changes
// (which always arrive as WS events, then trigger the refetch whose new data
// gets diffed) still do.
// eslint-disable-next-line no-unused-vars
export const useRemoteChangeSignal = (currentUserId) => {
  const lastEventAtRef = useRef(0);
  useEffect(() => webSocketService.addRecordChangeListener(() => {
    // Arm on EVERY event, own-actor ones included: the same user often has a
    // second window open, and its changes must flash here like anyone else's.
    // (An actor_user_id filter was tried and reverted — it killed exactly
    // that case.) Own edits in THIS tab still don't flash in the common case
    // because their optimistic cache write lands before their echo event
    // arrives, while the gate is still closed; the residual edge — a second
    // own edit within the window after one's own echo — is a rare cosmetic
    // false flash we accept. currentUserId is kept in the signature for
    // call-site stability.
    lastEventAtRef.current = Date.now();
  }), []);
  return () => Date.now() - lastEventAtRef.current < 8000;
};

// Mounted ONCE app-wide (next to useTranscodeUpdates in App): keeps the CRM
// list pages and their status-count badges live by invalidating the matching
// React Query caches when any record changes. Only stale-marks — active pages
// refetch, inactive caches refetch on next mount. Bursts (composite updates)
// collapse into one invalidation per second.
const LIST_KEYS_BY_ENTITY = {
  // QuotesList caches under 'listV2' (quotesService.listV2); 'list' is the
  // older useQuotes hook key — invalidate both. 'contact-counts' backs the
  // WhatsApp inbox contact panel (per-contact quote/sale/return counts).
  quote: [['quotes', 'list'], ['quotes', 'listV2'], ['quotes', 'statusCounts'], ['contact-counts']],
  sale: [['sales', 'list'], ['sales', 'statusCounts'], ['contact-counts']],
  crm_return: [['returns', 'list'], ['returns', 'statusCounts'], ['contact-counts']],
  // Line items render inline in quote/sale/return lists, the line-items list
  // and CRM task cards; which records show a given item isn't known here —
  // stale-mark them all (cheap: only active pages actually refetch).
  line_item: [
    ['quotes', 'list'], ['quotes', 'listV2'], ['sales', 'list'],
    ['returns', 'list'], ['lineItems', 'list'], ['crmTasks', 'list'],
  ],
};

export const useRecordChangeListInvalidation = (currentUserId) => {
  const queryClient = useQueryClient();
  const userIdRef = useRef(currentUserId);
  userIdRef.current = currentUserId;

  useEffect(() => {
    const pendingEntityTypes = new Set();
    const pendingQueryKeys = new Map();
    const inFlightKeys = new Set();
    let disposed = false;
    let timer = null;

    // Throttle window: caps a list tab at ~1 refetch / 1.5s no matter how fast
    // events stream (a courier-sync burst of hundreds of rows → one refetch,
    // not one per row). Non-resetting on purpose — a resetting debounce would
    // starve refetches under a continuous stream.
    const THROTTLE_MS = 1500;
    const isHidden = () => typeof document !== 'undefined' && document.visibilityState !== 'visible';

    const invalidateFor = (entityTypes) => {
      const keys = new Map(pendingQueryKeys);
      pendingQueryKeys.clear();
      for (const entityType of entityTypes) {
        for (const queryKey of LIST_KEYS_BY_ENTITY[entityType] || []) {
          keys.set(JSON.stringify(queryKey), queryKey);
        }
      }
      for (const [key, queryKey] of keys) {
        if (inFlightKeys.has(key)) {
          pendingQueryKeys.set(key, queryKey);
          continue;
        }
        // Finish slow reads instead of repeatedly cancelling/restarting them.
        // If the event overlaps an existing read, queue one catch-up read so
        // changes committed after that request began are not missed.
        if (queryClient.isFetching({ queryKey })) pendingQueryKeys.set(key, queryKey);
        inFlightKeys.add(key);
        const settled = () => {
          inFlightKeys.delete(key);
          if (!disposed && pendingQueryKeys.size) arm();
        };
        queryClient.invalidateQueries({ queryKey }, { cancelRefetch: false }).then(settled, settled);
      }
    };

    const flush = () => {
      timer = null;
      // Backgrounded tab: don't refetch (invalidateQueries would refetch even
      // hidden mounted lists). Keep the pending set; visibilitychange flushes
      // it on focus. Most of the fleet's tabs are backgrounded — this alone
      // cuts the active-refetch herd dramatically.
      if (isHidden()) return;
      invalidateFor(pendingEntityTypes);
      pendingEntityTypes.clear();
    };

    const arm = () => {
      if (!timer && !isHidden()) timer = setTimeout(flush, THROTTLE_MS);
    };

    const unsubscribe = webSocketService.addRecordChangeListener((event) => {
      // Record a foreign-user change so the self-mutation gate won't swallow a
      // colleague's flash (see selfMutationSignal.js). App-wide: this is the
      // one place that both sees every event AND knows the current user id.
      const me = userIdRef.current;
      if (event.actor_user_id != null && (me == null || Number(event.actor_user_id) !== Number(me))) {
        markForeignChange();
      }
      if (!LIST_KEYS_BY_ENTITY[event.entity_type]) return;
      pendingEntityTypes.add(event.entity_type);
      arm(); // no-op while hidden; the visibility handler catches up on focus
    });

    // Events missed while the socket was down never arrive — on reconnect,
    // stale-mark everything so open lists and counters resync (deferred if
    // hidden, like normal events).
    const unsubscribeReconnect = webSocketService.addReconnectListener(() => {
      for (const entityType of Object.keys(LIST_KEYS_BY_ENTITY)) pendingEntityTypes.add(entityType);
      if (isHidden()) return;
      arm();
    });

    const onVisible = () => {
      if (document.visibilityState === 'visible' && (pendingEntityTypes.size || pendingQueryKeys.size) && !timer) {
        timer = setTimeout(flush, 300); // small catch-up once the tab is back
      }
    };
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible);

    return () => {
      disposed = true;
      if (timer) clearTimeout(timer);
      unsubscribe();
      unsubscribeReconnect();
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible);
    };
  }, [queryClient]);
};

export default useRecordChanges;
